import React, { useState } from "react";

import { faXmark } from "@fortawesome/pro-regular-svg-icons";

import { Accordion, ButtonIcon, Chip, PanelBase } from "@performyard/py-components";
import type { CompetencyDictEntry } from "@performyard/service-contracts";

import PanelHeaderCard from "../PanelHeaderCard";

type LevelRow = { rank: number; name: string; description?: string; isPlaceholder: boolean };

type ViewLevelsPanelProps = {
	isOpen: boolean;
	onClose: () => void;
	competency: CompetencyDictEntry | undefined;
	currentLevel: number | null;
	expectedLevel: number | null;
	assignee?: { _id: string; name: string };
};

const ViewLevelsPanel = ({
	isOpen,
	onClose,
	competency,
	currentLevel,
	expectedLevel,
	assignee,
}: ViewLevelsPanelProps): React.ReactElement => {
	const [openRanks, setOpenRanks] = useState<Set<number>>(new Set());

	const toggleRank = (rank: number): void => {
		setOpenRanks((prev) => {
			const next = new Set(prev);
			if (next.has(rank)) {
				next.delete(rank);
			} else {
				next.add(rank);
			}
			return next;
		});
	};

	const levels = competency?.levels ?? [];
	const sortedLevels = [...levels].sort((a, b) => a.rank - b.rank);
	const minRank = sortedLevels.length > 0 ? sortedLevels[0].rank : null;
	const maxRank = sortedLevels.length > 0 ? sortedLevels[sortedLevels.length - 1].rank : null;

	// Archived assignments can carry a rank a later level-list shrink removed; show it as a
	// placeholder row (mirrors the grid/CSV "Level N" fallback) instead of silently dropping it.
	const realRanks = new Set(sortedLevels.map((level) => level.rank));
	const placeholderRanks = new Set(
		[currentLevel, expectedLevel].filter((rank): rank is number => rank !== null && !realRanks.has(rank))
	);
	const rows: LevelRow[] = [
		...sortedLevels.map((level) => ({ ...level, isPlaceholder: false as const })),
		...Array.from(placeholderRanks).map((rank) => ({ rank, name: `Level ${rank}`, isPlaceholder: true as const })),
	].sort((a, b) => a.rank - b.rank);

	return (
		<PanelBase isOpen={isOpen} onClose={onClose} side="right">
			<div className="flex h-full flex-col gap-4 overflow-y-auto p-6">
				<div className="flex items-start justify-between gap-2">
					<span className="text-paragraph-2 font-regular text-neutral-9">Competency Levels</span>
					<ButtonIcon
						variant="ghost"
						color="neutral"
						shade={5}
						size={24}
						icon={faXmark}
						onClick={onClose}
						aria-label="Close panel"
					/>
				</div>

				{competency && (
					<PanelHeaderCard
						name={competency.name}
						isCompanyWide={competency.is_company_wide}
						assignee={assignee}
						description={competency.description}
					/>
				)}

				{rows.length === 0 ? (
					<div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
						<p className="text-paragraph-2 text-neutral-6">No levels defined for this competency</p>
					</div>
				) : (
					<div className="flex flex-col">
						{rows.map((level, index) => {
							let subtitle = `Level ${level.rank}`;
							if (level.rank === minRank) subtitle += " (Lowest)";
							if (level.rank === maxRank) subtitle += " (Highest)";

							const isCurrentLevel = level.rank === currentLevel;
							const isExpectedLevel = level.rank === expectedLevel;
							const isLastLevel = rows.length > 1 && index === rows.length - 1;

							const headerContent = (
								<div className="flex min-w-0 flex-1 flex-col gap-1">
									<span className="text-paragraph-2 font-semibold [overflow-wrap:anywhere] text-neutral-9">
										{level.name}
									</span>
									<div className="flex flex-wrap items-center gap-1">
										<span className="text-caption text-neutral-6">{subtitle}</span>
										{isCurrentLevel && <Chip label="Current" color="green" size={20} />}
										{isExpectedLevel && <Chip label="Expected" color="primary" size={20} />}
									</div>
								</div>
							);

							return (
								<Accordion
									key={level.rank}
									data-testid={`level-${level.rank}`}
									variant="underline"
									isOpen={openRanks.has(level.rank)}
									onToggle={() => toggleRank(level.rank)}
									headerContent={headerContent}
									// Move the separator from under the header (the "underline" variant's default)
									// to the bottom of the whole item, so it sits after the description. Zero the
									// header's own bottom border and add one to the item wrapper (all but the last).
									className={`[&>button]:border-b-0 ${
										isLastLevel
											? ""
											: "border-x-0 border-t-0 border-b border-solid border-neutral-2"
									}`}
								>
									{level.isPlaceholder ? (
										<p className="text-paragraph-3 text-neutral-5">
											This level is no longer defined for this competency
										</p>
									) : level.description?.trim() ? (
										<p className="text-paragraph-3 [overflow-wrap:anywhere] whitespace-pre-wrap text-neutral-8">
											{level.description}
										</p>
									) : (
										<p className="text-paragraph-3 text-neutral-5">
											No description has been added for this level
										</p>
									)}
								</Accordion>
							);
						})}
					</div>
				)}
			</div>
		</PanelBase>
	);
};

export default ViewLevelsPanel;
